import React, {useEffect, useRef, useState} from 'react';
import {Config} from "../types/config";
import SliderWithInput from "./ui/SliderWithInput";
import {Switch, Tab, Tabs} from "@nextui-org/react";
import HotkeyButton from "./ui/HotkeyButton";
import AimIndicator from "./ui/AimIndicator";

// Дебаунсинг
const debounce = (func: Function, delay: number) => {
    let timer: NodeJS.Timeout;
    return (...args: any) => {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
    };
};


const ConfigUpdater = () => {
    const updateConfigRef = useRef<Function | null>(null);
    const [config, setConfig] = useState<Config>({
        AI: {
            AI_conf: 0.35,
            ai_model_image_size: 480
        },
        Mouse: {
            mouse_dpi: 3200,
            mouse_sensitivity: 3,
            mouse_fov_width: 60,
            mouse_fov_height: 45,
            mouse_random_range: 0
        },
        Aim: {
            hideout_targets: true,
            disable_headshot: false,
            body_y_offset: 0.35
        },
        Detection_window: {
            detection_window_width: 384,
            detection_window_height: 216
        },
        Hotkeys: {
            hotkey_targeting: "LeftMouseButton",
            hotkey_exit: "F2",
            hotkey_pause: "F3",
            hotkey_reload_config: 'F4'
        }
    });
    const handleUpdateConfig = async (
        value: number | string | boolean,
        section: keyof Config, // Уточнение типа для секции
        option: string
    ) => {
        try {
            setConfig(prevConfig => {
                const updatedSection = {
                    ...prevConfig[section],
                    [option]: value // Приводим значение к нужному типу
                };
                //updateConfig({...prevConfig, [section]: updatedSection})
                debouncedUpdateConfig.current({ ...prevConfig, [section]: updatedSection });
                return {
                    ...prevConfig,
                    [section]: updatedSection,
                };
            });
        } catch (error) {
            console.error('Error updating config:', error);
        }
    };
    const updateConfig = async (config: Config) => {
        try {
            const response = await window.pywebview.api.updateConfig(config);
            console.log('Config updated:', response);
        }catch (e) {

        }
    }
    const debouncedUpdateConfig = useRef(debounce(updateConfig, 500));
    const getConfig = async () => {
        try {
            const response = await window.pywebview.api.getConfig();
            setConfig(JSON.parse(response.data));
        }catch (e) {

        }
    }
    useEffect(() => {
        setTimeout(()=>{
            getConfig().then();
        },500)
    }, []);

    return (
        <div className={'p-4 overflow-x-hidden flex flex-col items-center'}>
            <Tabs defaultSelectedKey={"ai"} aria-label="Options">
                <Tab key="ai" title="Ai">
                    <div>
                        <SliderWithInput
                            label={'AI Confidence'}
                            maxValue={0.50}
                            minValue={0}
                            onChange={(e) => handleUpdateConfig(e, 'AI', 'AI_conf')}
                            value={config.AI.AI_conf}
                            description={"The confidence threshold for AI decisions. Higher values mean the AI needs more certainty to take action."}
                        />
                        <SliderWithInput
                            label={'AI Model Image Size'}
                            maxValue={640}
                            minValue={320}
                            onChange={(e) => handleUpdateConfig(e, 'AI', 'ai_model_image_size')}
                            value={config.AI.ai_model_image_size}
                            description={"The size of the image fed into the AI model."}
                        />
                    </div>
                </Tab>
                <Tab key="aim" title="Aim">
                    <div>
                        <SliderWithInput
                            label={'Speed'}
                            maxValue={40000}
                            minValue={20}
                            onChange={(e) => handleUpdateConfig(e, 'Mouse', 'mouse_dpi')}
                            value={config.Mouse.mouse_dpi}
                            description={"Aiming speed"}
                        />
                        <SliderWithInput
                            label={'Sensitivity'}
                            maxValue={50}
                            minValue={1}
                            onChange={(e) => handleUpdateConfig(e, 'Mouse', 'mouse_sensitivity')}
                            value={config.Mouse.mouse_sensitivity}
                            description={"The value by which to divide the mouse coordinates, works as mouse sensitivity, for example, if the value is set to 2, the coordinates will be divided by 2 and the mouse will move slower."}
                        />
                        <SliderWithInput
                            label={'FOV width'}
                            maxValue={100}
                            minValue={10}
                            onChange={(e) => handleUpdateConfig(e, 'Mouse', 'mouse_fov_width')}
                            value={config.Mouse.mouse_fov_width}
                            description={"The width of the field of view (FOV) for mouse targeting."}
                        />
                        <SliderWithInput
                            label={'FOV height'}
                            maxValue={100}
                            minValue={10}
                            onChange={(e) => handleUpdateConfig(e, 'Mouse', 'mouse_fov_height')}
                            value={config.Mouse.mouse_fov_height}
                            description={"The height of the field of view (FOV) for mouse targeting."}
                        />
                        <SliderWithInput
                            label={'Random range'}
                            maxValue={29}
                            minValue={0}
                            onChange={(e) => handleUpdateConfig(e, 'Mouse', 'mouse_random_range')}
                            value={config.Mouse.mouse_random_range}
                            description={'The range of random movement for the mouse.'}
                        />
                        <div className={'flex items-center w-full'}>
                            <SliderWithInput
                                label={'Aiming height'}
                                maxValue={0.40}
                                minValue={0.10}
                                onChange={(e) => handleUpdateConfig(e, 'Aim', 'body_y_offset')}
                                value={config.Aim.body_y_offset}
                                description={'Y-axis offset for aiming at the body of the target. '}
                            />
                            <AimIndicator offsetY={config.Aim.body_y_offset} />
                        </div>
                        <div className="flex flex-col gap-2 mb-2">
                            <Switch
                                onValueChange={(e) => handleUpdateConfig(e, 'Aim', 'disable_headshot')}
                                isSelected={config.Aim.disable_headshot}
                            >
                                Mode
                            </Switch>
                            <p className="text-small text-default-500">Selected: {config.Aim.disable_headshot ? "Aiming at the body" : "Aiming at the head"}</p>
                        </div>
                        <div className="flex flex-col gap-2 mb-2">
                            <Switch
                                onValueChange={(e) => handleUpdateConfig(e, 'Aim', 'hideout_targets')}
                                isSelected={config.Aim.hideout_targets}
                            >
                                Targets at the polygon
                            </Switch>
                            <p className="text-small text-default-500">Selected: {config.Aim.hideout_targets ? "Shown" : "Hidden"}</p>
                        </div>
                    </div>
                </Tab>
                <Tab key="hotkeys" title="Hotkeys">
                    <div>
                        <HotkeyButton
                            label="Targeting Hotkey"
                            currentKey={config.Hotkeys.hotkey_targeting}
                            onUpdateKey={(newKey) => handleUpdateConfig(newKey, "Hotkeys", "hotkey_targeting")}
                            allBindings={new Set(Object.values(config.Hotkeys))}
                        />
                        <HotkeyButton
                            label="Exit Hotkey"
                            currentKey={config.Hotkeys.hotkey_exit}
                            onUpdateKey={(newKey) => handleUpdateConfig(newKey, "Hotkeys", "hotkey_exit")}
                            allBindings={new Set(Object.values(config.Hotkeys))}
                        />
                        <HotkeyButton
                            label="Pause Hotkey"
                            currentKey={config.Hotkeys.hotkey_pause}
                            onUpdateKey={(newKey) => handleUpdateConfig(newKey, "Hotkeys", "hotkey_pause")}
                            allBindings={new Set(Object.values(config.Hotkeys))}
                        />
                        <HotkeyButton
                            label="Reload config hotkey"
                            currentKey={config.Hotkeys.hotkey_reload_config}
                            onUpdateKey={(newKey) => handleUpdateConfig(newKey, "Hotkeys", "hotkey_reload_config")}
                            allBindings={new Set(Object.values(config.Hotkeys))}
                        />
                    </div>
                </Tab>
                <Tab key="detection_window" title="Detection window">
                    <div>
                        <SliderWithInput
                            label={'Detection window height'}
                            maxValue={600}
                            minValue={50}
                            onChange={(e) => handleUpdateConfig(e, 'Detection_window', 'detection_window_height')}
                            value={config.Detection_window.detection_window_height}
                            description={"Height of the detection window in pixels, where the image analysis will occur. Affects performance."}
                        />
                        <SliderWithInput
                            label={'Detection window width'}
                            maxValue={600}
                            minValue={50}
                            onChange={(e) => handleUpdateConfig(e, 'Detection_window', 'detection_window_width')}
                            value={config.Detection_window.detection_window_width}
                            description={"Width of the detection window in pixels, where the image analysis will occur. Affects performance."}
                        />
                    </div>
                </Tab>
            </Tabs>
        </div>
    );
};

export default ConfigUpdater;
